import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, AlertTriangle, RefreshCw, Loader2, ExternalLink, Copy } from "lucide-react";

// Classes Tailwind literais (o purge não resolve `bg-${x}-100` dinâmico).
const STATUS_META = {
  approved:         { icon: CheckCircle2, label: "Aprovado",           bg: "bg-emerald-100", txt: "text-emerald-700", chip: "bg-emerald-100 text-emerald-800" },
  pending:          { icon: Clock,        label: "Pendente Meta",      bg: "bg-amber-100",   txt: "text-amber-700",   chip: "bg-amber-100 text-amber-800" },
  rejected:         { icon: XCircle,      label: "Rejeitado",          bg: "bg-red-100",     txt: "text-red-700",     chip: "bg-red-100 text-red-800" },
  received:         { icon: Clock,        label: "Recebido (aguarda)", bg: "bg-sky-100",     txt: "text-sky-700",     chip: "bg-sky-100 text-sky-800" },
  unsubmitted:      { icon: AlertTriangle,label: "Não submetido",      bg: "bg-slate-100",   txt: "text-slate-700",   chip: "bg-slate-100 text-slate-800" },
  sid_invalid:      { icon: XCircle,      label: "SID inválido no .env", bg: "bg-red-100",   txt: "text-red-700",     chip: "bg-red-100 text-red-800" },
  not_configured:   { icon: AlertTriangle,label: "Env ausente",        bg: "bg-slate-100",   txt: "text-slate-700",   chip: "bg-slate-100 text-slate-800" },
  approval_unknown: { icon: AlertTriangle,label: "Sem approval",       bg: "bg-slate-100",   txt: "text-slate-700",   chip: "bg-slate-100 text-slate-800" },
  unknown:          { icon: AlertTriangle,label: "Desconhecido",       bg: "bg-slate-100",   txt: "text-slate-700",   chip: "bg-slate-100 text-slate-800" },
};

export default function TemplatesHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/outreach/templates-health");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao consultar Twilio");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (!data && !loading) return null;

  const rows = data?.templates || [];
  const approvedCount = rows.filter(r => r.status === "approved").length;
  const pendingCount = rows.filter(r => ["pending", "received"].includes(r.status)).length;
  const brokenCount = rows.filter(r => ["rejected", "sid_invalid", "not_configured"].includes(r.status)).length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="templates-health-panel">
      <div className="p-4 border-b border-slate-200 flex items-center gap-3">
        <div>
          <h2 className="font-display font-bold text-lg text-[#0B2B62]">Templates WhatsApp — Status Meta</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Puxa direto do Twilio Content API. Só template <b>approved</b> envia fora da janela 24h.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="templates-health-refresh"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#0B2B62] disabled:opacity-60">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Atualizar
        </button>
      </div>

      {loading && !data && (
        <div className="p-8 text-center text-slate-500 text-sm">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
          Consultando Twilio…
        </div>
      )}

      {data && !data.configured && (
        <div className="p-4 bg-red-50 border-b border-red-200 text-sm text-red-800">
          <b>Twilio não configurado</b>: {data.reason}
        </div>
      )}

      {data && data.configured && (
        <>
          <div className="grid grid-cols-3 gap-0 border-b border-slate-200">
            <div className="p-3 text-center border-r border-slate-200">
              <div className="text-2xl font-display font-bold text-emerald-600" data-testid="templates-approved-count">{approvedCount}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Aprovados</div>
            </div>
            <div className="p-3 text-center border-r border-slate-200">
              <div className="text-2xl font-display font-bold text-amber-600" data-testid="templates-pending-count">{pendingCount}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Pendentes Meta</div>
            </div>
            <div className="p-3 text-center">
              <div className="text-2xl font-display font-bold text-red-600" data-testid="templates-broken-count">{brokenCount}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Precisam ação</div>
            </div>
          </div>

          {data.wa_from && (
            <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-200">
              Remetente configurado: <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-[10px]" data-testid="templates-wa-from">{data.wa_from}</code>
              {data.wa_from?.includes("14155238886") && (
                <span className="ml-2 text-red-600 font-bold">⚠ SANDBOX detectado — não é produção!</span>
              )}
            </div>
          )}

          <div className="divide-y divide-slate-100">
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.unknown;
              const Icon = meta.icon;
              return (
                <div key={r.label} className="p-3 flex items-start gap-3" data-testid={`template-row-${r.label}`}>
                  <div className={`mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${meta.bg}`}>
                    <Icon className={`w-4 h-4 ${meta.txt}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-[#0B2B62]">{r.label}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${meta.chip}`} data-testid={`template-status-${r.label}`}>
                        {meta.label}
                      </span>
                      {r.category && (
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-1 py-0.5 rounded border ${r.category === "UTILITY" ? "border-sky-300 text-sky-700" : "border-fuchsia-300 text-fuchsia-700"}`}>
                          {r.category}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">{r.description}</p>
                    <div className="text-[10px] text-slate-400 mt-1 font-mono flex items-center gap-2 flex-wrap">
                      <span>{r.env_var}</span>
                      {r.content_sid && (
                        <>
                          <span>·</span>
                          <code className="bg-slate-100 px-1 rounded">{r.content_sid}</code>
                          <button onClick={() => { navigator.clipboard.writeText(r.content_sid); toast.success("SID copiado"); }}
                            className="hover:text-slate-700" title="Copiar SID">
                            <Copy className="w-3 h-3" />
                          </button>
                        </>
                      )}
                      {r.friendly_name && <span>· {r.friendly_name}</span>}
                    </div>
                    {r.rejection_reason && r.rejection_reason !== "Unknown rejection reason" && (
                      <div className="mt-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                        <b>Motivo Meta:</b> {r.rejection_reason}
                      </div>
                    )}
                    {r.status === "pending" && (
                      <div className="mt-1 text-[11px] text-amber-800">
                        Aguardando aprovação do Meta{r.category === "MARKETING" ? " (categoria MARKETING costuma levar 24-72h)" : " (UTILITY: 1-24h)"}.
                      </div>
                    )}
                    {r.status === "rejected" && (
                      <div className="mt-1 text-[11px] text-red-800">
                        Template rejeitado. Abra no Twilio Console, ajuste o corpo e reenvie pra aprovação.
                      </div>
                    )}
                    {r.status === "sid_invalid" && (
                      <div className="mt-1 text-[11px] text-red-800">
                        SID no <code>.env</code> não existe nesta conta Twilio. Copie o SID correto do Console e atualize a variável de ambiente.
                      </div>
                    )}
                    {r.status === "not_configured" && (
                      <div className="mt-1 text-[11px] text-slate-600">
                        Variável não preenchida em <code>backend/.env</code>. Sem isso, nada é enviado via template.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-3 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-600 leading-relaxed">
            <b>Para submeter template pendente:</b> Twilio Console → <i>Content Templates</i> → clique no template → <i>Submit for WhatsApp Approval</i>. Depois retorne aqui e clique <b>Atualizar</b>.
            {" "}
            <a href="https://console.twilio.com/us1/develop/sms/content-editor/templates" target="_blank" rel="noreferrer" className="text-[#0B2B62] font-semibold inline-flex items-center gap-1 hover:underline">
              Abrir Twilio Console <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </>
      )}
    </div>
  );
}
